import { useState, useEffect } from "react";

const logoUrl = (t) => `https://images.financialmodelingprep.com/symbol/${encodeURIComponent(t)}.png`;

// Cache: ticker -> "light" (white-ish logo, needs dark tile) | "dark" (needs white tile) | "failed"
const toneCache = new Map();

function detectTone(img) {
  try {
    const size = 24;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, size, size);
    const { data } = ctx.getImageData(0, 0, size, size);
    let opaque = 0, nearWhite = 0;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] > 40) {
        opaque++;
        if ((data[i] + data[i + 1] + data[i + 2]) / 3 > 215) nearWhite++;
      }
    }
    if (!opaque) return "light";
    return nearWhite / opaque > 0.55 ? "light" : "dark";
  } catch {
    return "dark";
  }
}

export default function CompanyLogo({ ticker, size = 32 }) {
  const [state, setState] = useState(() => toneCache.get(ticker) || "loading");

  useEffect(() => {
    if (!ticker) return;
    const cached = toneCache.get(ticker);
    if (cached) { setState(cached); return; }
    let alive = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const tone = detectTone(img);
      toneCache.set(ticker, tone);
      if (alive) setState(tone);
    };
    img.onerror = () => {
      toneCache.set(ticker, "failed");
      if (alive) setState("failed");
    };
    img.src = logoUrl(ticker);
    return () => { alive = false; };
  }, [ticker]);

  if (!ticker || state === "failed") {
    return (
      <div
        className="rounded-lg flex items-center justify-center shrink-0 font-bold text-zinc-300 select-none"
        style={{
          width: size, height: size, fontSize: Math.max(8, size * 0.3),
          background: "rgba(96,165,250,0.10)", border: "1px solid rgba(96,165,250,0.20)",
        }}
        data-testid={`logo-fallback-${ticker || "unknown"}`}
      >
        {ticker ? ticker.slice(0, 2) : "?"}
      </div>
    );
  }

  if (state === "loading") {
    return (
      <div
        className="rounded-lg shrink-0 animate-pulse"
        style={{ width: size, height: size, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.06)" }}
        data-testid={`logo-loading-${ticker}`}
      />
    );
  }

  const lightLogo = state === "light";
  return (
    <div
      className="rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
      style={{
        width: size, height: size,
        background: lightLogo ? "#1A2029" : "#FFFFFF",
        border: "1px solid rgba(255,255,255,0.10)",
        padding: Math.max(3, Math.round(size * 0.14)),
      }}
      data-testid={`logo-${ticker}`}
    >
      <img
        src={logoUrl(ticker)}
        alt={`${ticker} logo`}
        className="w-full h-full object-contain"
        draggable={false}
        onError={() => { toneCache.set(ticker, "failed"); setState("failed"); }}
      />
    </div>
  );
}
